import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";

import trailCreatedIcon from "../../img/eventTrailCreatedIcon.svg";
import trailUpdatedIcon from "../../img/eventTrailUpdatedIcon.svg";
import trailCancelledIcon from "../../img/eventTrailCancelledIcon.svg";

const DottedLine = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "40px",
  top: "80px",
  width: "2px",
  height: "calc(100% - 65px + 22px)",
  background:
    "repeating-linear-gradient(to bottom, #9ca3af 0px, #9ca3af 4px, transparent 4px, transparent 8px)",
  zIndex: 0,
}));

function formatDateTime(timestampOrString) {
  const date = new Date(timestampOrString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const mins = String(date.getMinutes()).padStart(2, "0");
  const period = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12; // Convert to 12-hour format
  const hoursStr = String(hours).padStart(2, "0");

  return `${day}/${month}/${year} at ${hoursStr}:${mins} ${period}`;
}

function decorateTrail(selectedTrail = [], isActive = true) {
  const lastIdx = selectedTrail.length - 1;

  return selectedTrail.map((item, idx) => {
    // 1. Determine status
    let status;

    // if (!isActive && idx === 0) {
    //   status = "Cancelled";
    // } else if (idx === lastIdx) {
    //   status = "Created";
    // } else {
    //   status = "Updated";
    // }

    if (idx === 0) {
      status = "Created";
    } else if (isActive && idx === lastIdx) {
      status = "Updated";
    } else if (!isActive && idx === lastIdx) {
      status = "Cancelled";
    } else {
      status = "Updated";
    }

    // 2. Pick colors/icons based on status
    let color, iconColor, textColor, customIcon;
    switch (status) {
      case "Cancelled":
        color = "#fdeaea";
        iconColor = "red";
        textColor = "red";
        customIcon = trailCancelledIcon;
        break;
      case "Updated":
        color = "#eaf3fd";
        iconColor = "#1976d2";
        textColor = "#1976d2";
        customIcon = trailUpdatedIcon;
        break;
      case "Created":
        color = "#e8f7ec";
        iconColor = "green";
        textColor = "green";
        customIcon = trailCreatedIcon;
        break;
    }

    // 3. Return a new "decorated" object
    return {
      ...item,
      status,
      color,
      iconColor,
      textColor,
      customIcon,
    };
  });
}

const ViewTrailDialog = ({
  openTrail = false,
  minimalData = { user: null, isActive: false },
  selectedTrail = [],
  handleCloseTrail = () => {},
}) => {
  const modifiedEventTrail = decorateTrail(
    selectedTrail,
    minimalData?.isActive,
  );
  return (
    <Dialog
      open={openTrail}
      onClose={handleCloseTrail}
      fullWidth
      sx={{
        height: "650px",
        top: "100px",
        "& .MuiPaper-root": {
          borderRadius: "16px",
          width: "700px",
          maxWidth: "90vw",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: "#0e4a87",
          padding: "10px 20px",
          color: "white",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography variant="h4" sx={{ fontSize: "24px" }}>
              Changes Trail
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "17px" }}>
              {minimalData && minimalData?.user && minimalData?.user?.name
                ? minimalData?.user?.name
                : "NA"}
              {`${minimalData?.user?.empCode ? `(${minimalData?.user?.empCode})` : ""}`}
            </Typography>
          </Box>

          <IconButton
            onClick={() => handleCloseTrail()}
            sx={{ color: "white" }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ py: 3, width: "100%" }}>
        <Stack spacing={2}>
          {modifiedEventTrail?.map((item, index) => (
            <Box key={index} sx={{ position: "relative" }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    bgcolor: item?.color,
                    border: `2px solid ${item?.iconColor}`,
                    width: 80,
                    height: 80,
                    zIndex: 2,
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={item?.customIcon}
                    alt="eventIcon"
                    style={{
                      width: 40,
                      height: 40,
                      marginLeft: "1px",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                {/* Info Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: item?.color,
                    flex: 1,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: item?.textColor }}
                  >
                    {item?.status}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                    <PersonOutlineOutlinedIcon
                      fontSize="small"
                      sx={{ color: item?.textColor }}
                    />
                    <Typography variant="body1" color={item?.textColor}>
                      {item?.updatedUser?.name || ""}
                      {`(${item?.updatedUser?.empCode || ""})`}
                    </Typography>
                    <AccessTimeIcon
                      fontSize="small"
                      sx={{ color: item?.textColor, marginLeft: "10px" }}
                    />
                    <Typography variant="body1" color={item?.textColor}>
                      {item.date}
                      {formatDateTime(item?.createdAt)}
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>

              {/* Dotted Line - only show if not the last item */}
              {index < modifiedEventTrail?.length - 1 && <DottedLine />}
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ViewTrailDialog;
